import { CRAYO_ENTRY, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;

export const RIVERSIDE_CONTENT: CompareContent = {
  slug: "crayo-vs-riverside-magic-clips",
  competitorSlug: "riverside-magic-clips",
  title: "Crayo vs Riverside Magic Clips",
  seoTitle: "Crayo vs Riverside Magic Clips: Pricing and Features Compared",
  seoDescription:
    "Crayo vs Riverside Magic Clips, prices dated. Magic Clips only clips recordings made in Riverside. Crayo ranks clips from any YouTube or TikTok link or file.",
  cardVerdict:
    "Better than Crayo for recording, free tier and 4K, behind on source flexibility and moment ranking.",
  bestFor: "Podcasters who record in Riverside",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick for clippers working from any podcast or interview, whatever recorded it: AutoClip takes a YouTube or TikTok link or a file, ranks the moments, reframes faces and charges one credit per finished clip, ${money(hobby.monthly)} a month for ${hobby.credits}. Riverside Magic Clips is the better pick if you already record in Riverside, want a free tier with clipping, or need 4K and live streaming. The evidence is below.`,
  pickCrayoIf: [
    "You clip podcasts that were not recorded in Riverside: Crayo takes YouTube and TikTok links or a file upload up to 4 GB and 3 hours, and Magic Clips only runs on content recorded or uploaded inside Riverside.",
    "You want the strongest moments ranked before you cut: AutoClip and Find Viral Moments rank every moment with reasons, and Magic Clips shows no score or ranking.",
    "You want a face-aware camera on single-camera footage, 32 caption styles with keyword highlight in 56 spoken languages, a timeline editor and a 34-endpoint API on every plan, with 1080p exports and no watermark.",
  ],
  pickThemIf: [
    "You need the recording studio: Riverside records 4K local multitrack and streams live to YouTube, Twitch, Facebook and LinkedIn. Crayo records nothing.",
    "You want a free tier with clipping: Riverside Free includes Magic Clips at 720p with a watermark. Crayo has no free plan and no trial.",
    "You want captions in 100+ languages, podcast hosting with analytics, mobile recording apps, or clips that spend no credits. Crayo transcribes 56 languages and charges 1 credit per clip.",
  ],
  wins: [
    {
      claim: "Crayo clips any YouTube or TikTok link or file; Magic Clips only clips what was recorded or uploaded inside Riverside.",
      evidence:
        "Riverside's Magic Clips page, checked Oct 8, 2026, describes clipping content recorded or uploaded inside Riverside and lists no YouTube-link, Twitch or Kick import. Crayo AutoClip takes YouTube and TikTok links or a file upload up to 4 GB and 3 hours, so a Riverside recording can be downloaded and clipped in Crayo: record there, clip here.",
      href: "https://riverside.com/magic-clips",
    },
    {
      claim: "Crayo ranks moments; Magic Clips shows no ranking or score.",
      evidence:
        "The Magic Clips page, checked Oct 8, 2026, promises AI clips made to go viral but shows no score or ordering per clip. A review dated Oct 1, 2026 put usable output at about 1 in 4 suggested clips. Crayo's AutoClip and Find Viral Moments rank every moment with the planner's reasons attached, and Find Viral Moments lets you review up to 50 ranked moments before spending a credit on any of them.",
      href: "https://www.creatorstackclub.com/software/riverside",
    },
    {
      claim: "Crayo ships 32 caption styles with keyword highlight; Magic Clips publishes no style count.",
      evidence:
        "The Magic Clips page, checked Oct 8, 2026, lists font, color, size, placement and movement controls for captions and no count of presets. Crayo's editor has 32 caption styles with keyword highlight on every plan, and AutoClip applies one of them automatically.",
      href: "https://riverside.com/magic-clips",
    },
    {
      claim: "Crayo's camera is face-aware on single-camera footage; Riverside's layouts switch by active speaker in multicam recordings.",
      evidence:
        "Riverside's smart layouts, per its Magic Clips page checked Oct 8, 2026, change camera by whoever is speaking in a multi-camera recording, with a focus speaker or keywords chosen by you. No single-camera face-tracking claim appears on the page. Crayo's AutoClip tracks faces in the frame and keeps the speaker in the crop across speaker, split and full layouts.",
      href: "https://riverside.com/magic-clips",
    },
    {
      claim: "Crayo has a 34-endpoint API with webhooks on every plan; Riverside has no public API.",
      evidence:
        "Riverside's pricing and feature pages, checked Oct 8, 2026, show integration running through Zapier rather than a public API. Every Crayo plan includes the API and webhooks, with docs at crayo.ai/developers, so a clipping workflow can be scripted from the entry plan up.",
      href: "https://crayo.ai/developers",
    },
  ],
  concessions: [
    {
      claim: "Riverside is the recording studio; Crayo is not.",
      evidence:
        "Riverside records 4K local multitrack audio and video with each guest captured on their own track, and streams live to YouTube, Twitch, Facebook, LinkedIn and custom RTMP, per its pricing page checked Oct 8, 2026. Crayo starts from a finished file or link and records nothing.",
      href: "https://riverside.com/pricing",
    },
    {
      claim: "Riverside has a free tier that includes Magic Clips.",
      evidence:
        "Riverside Free is $0 with 2 hours of multitrack recording once, unlimited single-track recording, 720p with a watermark and Magic Clips, per its pricing page checked Oct 8, 2026. Crayo has no free plan and no trial.",
      href: "https://riverside.com/pricing",
    },
    {
      claim: "Riverside captions in 100+ languages and exports 4K; Crayo transcribes 56 languages and tops out at 1080p.",
      evidence:
        "Riverside's Magic Clips page advertises AI captions in 100+ languages, and Pro and above export up to 4K, per its pricing page checked Oct 8, 2026. Crayo's language picker has 56 entries and exports at 1080p maximum.",
      href: "https://riverside.com/magic-clips",
    },
    {
      claim: "Magic Clips spend no credits, and Pro adds podcast hosting, analytics, text-based editing and mobile recording apps.",
      evidence:
        "Riverside's pricing page, checked Oct 8, 2026, says Magic Clips, Magic Audio and AI Co-Creator do not require credits, and Pro at $29 a month includes podcast hosting with analytics and unlimited transcription with text-based editing. Riverside has iOS and Android recording apps. Crayo charges 1 workflow credit per clip, hosts nothing and is a browser app.",
      href: "https://riverside.com/pricing",
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes, from any YouTube or TikTok link or file`,
      note: "Prices from the live plan table. Crayo does not record, so the episodes come from wherever you record them.",
    },
    them: {
      plan: "Pro",
      cost: "$29/mo or $24/mo yearly ($288/yr)",
      usage: "4 of 15 separate-track download hours; the 32 Magic Clips spend none of the 2,000 AI credits",
      note: "Only if all four episodes were recorded or uploaded in Riverside. Both tools can be used together: record in Riverside, clip in either.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 45-minute two-person podcast recording owned by Crayo, uploaded to Riverside for the Magic Clips run",
    plans: "Riverside Pro and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "Riverside meters separate-track download hours per month, which do not roll over, plus AI credits for translation, AI B-roll and animated clips; Magic Clips need no credits and recording itself is unlimited. Refund and cancellation terms are not on the pricing page, and the page's Webinar annual line conflicts with its FAQ.",
  faqs: [
    {
      question: "Is Crayo cheaper than Riverside Magic Clips?",
      answer:
        `It depends on where you record. Crayo Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year for ${hobby.credits} credits, and one credit is one finished clip. Riverside Pro is $29 a month or $24 a month billed annually at $288, checked Oct 8, 2026, and Magic Clips spend no credits on top of that. If you already pay for Riverside to record, its clips cost nothing extra. If you record anywhere else, Riverside cannot clip that footage at all, and Crayo Hobby covers 32 clips a month from any YouTube or TikTok link or file with credits to spare.`,
    },
    {
      question: "Does Crayo have a free plan like Riverside?",
      answer:
        `No. Crayo has no free plan and no trial; the only free products are twelve browser tools at crayo.ai/features/free that need no account and never upload the file. The entry plan is Hobby at ${money(hobby.monthly)} a month. Riverside Free is $0 with 2 hours of multitrack recording once, unlimited single-track recording, 720p with a watermark and Magic Clips included, per its pricing page checked Oct 8, 2026. If you want to try auto-clipping before paying anything, Riverside wins that row.`,
    },
    {
      question: "Can Riverside Magic Clips clip a YouTube video or a Twitch stream?",
      answer:
        "No. Riverside's Magic Clips page, checked Oct 8, 2026, describes clipping content recorded or uploaded inside Riverside, and lists no YouTube-link, Twitch or Kick import. You would download the video and upload it into a Riverside project first. Crayo AutoClip takes a YouTube or TikTok link directly, or a file up to 4 GB and 3 hours. Neither tool imports Twitch or Kick VODs by link.",
    },
    {
      question: "Does Riverside Magic Clips have a virality score?",
      answer:
        "No. The Magic Clips page, checked Oct 8, 2026, says the AI picks a recording's highlights automatically but shows no score or ranking per clip, and a review dated Oct 1, 2026 said about 1 in 4 suggested clips was worth posting. Crayo ranks moments rather than printing a number: AutoClip orders its clips by strength with the planner's reasons attached, and Find Viral Moments shows up to 50 ranked moments to review before any credit is spent.",
    },
    {
      question: "Can I use Crayo and Riverside together?",
      answer:
        "Yes, and many podcast clippers will. Record the episode in Riverside, where each guest lands on a separate 4K track, download the finished file, and upload it to Crayo. AutoClip returns 1 to 20 ranked clips at 15 to 120 seconds each with captions, a face-aware camera and a layout you choose. Nothing has to migrate; your recording stays in Riverside and your clips export from Crayo at 1080p.",
    },
    {
      question: "Does Crayo record podcasts like Riverside?",
      answer:
        "No. Crayo does not record audio or video, host a podcast, or stream live. Riverside, which moved from riverside.fm to riverside.com, records 4K local multitrack with a separate track per guest, streams to YouTube, Twitch, Facebook and LinkedIn, and hosts shows with analytics on Pro and above, per its pricing page checked Oct 8, 2026. Crayo starts after the recording exists: paste a link or upload the file and it clips, captions, reframes and edits.",
    },
    {
      question: "Does Crayo export 4K like Riverside?",
      answer:
        "No. Crayo exports up to 1080p on every plan. Riverside exports up to 4K on Pro and above and 720p with a watermark on Free, per its pricing page checked Oct 8, 2026. For vertical clips posted to TikTok, Reels and Shorts, 1080p is the delivered resolution on those platforms, but if you need 4K masters, Riverside wins that row.",
    },
    {
      question: "Does Crayo auto-post clips like Riverside?",
      answer:
        "No. Crayo exports the file and you post it yourself; there is no scheduler. Riverside lists Social Scheduling in its navigation, checked Oct 8, 2026, though its Magic Clips page does not describe scheduling from the clip view. Crayo's View Tracker watches Instagram, YouTube and TikTok creators for cadence and viral outliers, which is research, not a scheduler and not per-post analytics.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Riverside prices checked Oct 8, 2026: Free $0 with Magic Clips at 720p, Pro $29/mo or $24/mo billed annually at $288, Grow $39/mo or $34/mo billed annually at $408.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-descript", "crayo-vs-opus-clip", "crayo-vs-vizard"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "riverside magic clips",
    "riverside alternative for clips",
    "riverside vs crayo",
    "riverside.fm clips",
    "riverside pricing",
    "crayo vs riverside",
  ],
};
